import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FEATURE_TO_USED_IN,
  LLM_MODEL_LIST_PRICES,
  isImageModel,
  isImageUsageFeature,
  listPriceFor,
  projectCost,
  DEFAULT_THINKING_SHARE,
} from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nexus/ui";
import type { LlmUsageFeatureTotals } from "@/features/llm-usage/api/llm-usage.api";
import { costDisplay } from "../lib/cost-display";
import { translateLlmUsageFeature } from "../lib/llm-usage-feature-labels";

interface ModelSwitchSimulatorDialogProps {
  feature: LlmUsageFeatureTotals | null;
  onClose: () => void;
}

const THINKING_SHARE_STEPS = [0, 0.25, 0.5, 0.75] as const;

/**
 * What this feature would have cost on another model. It does not change the
 * prompt; the link is the place that does.
 */
export function ModelSwitchSimulatorDialog({ feature, onClose }: ModelSwitchSimulatorDialogProps) {
  const { t, i18n } = useTranslation();
  const isImage = isImageUsageFeature(feature?.feature);
  const models = useMemo(() => {
    const seen = new Set<string>();
    return LLM_MODEL_LIST_PRICES.filter((price) => {
      if (seen.has(price.model)) return false;
      seen.add(price.model);
      return isImage ? isImageModel(price.model) : !isImageModel(price.model);
    });
  }, [isImage]);

  const [targetModel, setTargetModel] = useState<string>("");
  const [thinkingShare, setThinkingShare] = useState(DEFAULT_THINKING_SHARE);
  // The dialog stays mounted between rows, so a pick made for one feature would
  // otherwise open as the starting point for the next one.
  const featureKey = feature ? (feature.feature ?? "") : null;
  useEffect(() => {
    setTargetModel("");
    setThinkingShare(DEFAULT_THINKING_SHARE);
  }, [featureKey]);
  const selected = targetModel || models[0]?.model || "";
  const rates = selected ? listPriceFor(selected.startsWith("claude") ? "ANTHROPIC" : "GEMINI", selected) : null;

  const projection = useMemo(() => {
    if (!feature || !rates || !selected) return null;
    return projectCost({
      inputTokens: feature.inputTokens,
      outputTokens: feature.outputTokens,
      thinkingTokens: feature.thinkingTokens,
      cacheWriteTokens: feature.cacheWriteTokens,
      cacheReadTokens: feature.cacheReadTokens,
      calls: Math.max(0, feature.calls - feature.failedCalls),
      fromModel: feature.primaryModel ?? selected,
      toModel: selected,
      rates,
      thinkingShare: isImage ? undefined : thinkingShare,
      isImage,
    });
  }, [feature, rates, selected, thinkingShare, isImage]);

  const locale = i18n.language;
  const today = feature ? costDisplay({ costMicros: feature.costMicros, unpricedTokens: feature.unpricedTokens }, locale) : null;
  const projected = projection
    ? costDisplay({ costMicros: projection.costMicros, unpricedTokens: 0 }, locale)
    : null;
  const deltaMicros = projection && feature ? projection.costMicros - feature.costMicros : 0;
  const delta = costDisplay({ costMicros: Math.abs(deltaMicros), unpricedTokens: 0 }, locale);
  const usedIn = feature?.feature ? FEATURE_TO_USED_IN[feature.feature as keyof typeof FEATURE_TO_USED_IN] : undefined;
  const label = feature ? translateLlmUsageFeature(t, feature.feature ?? "") : "";

  return (
    <Dialog open={feature != null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("admin.llmUsage.switchModel.title", "Switch model")}</DialogTitle>
          <DialogDescription>
            {t("admin.llmUsage.switchModel.description", "Projected cost for {{feature}} on another model. Nothing is changed.", {
              feature: label,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex flex-col gap-1">
            <Label htmlFor="llm-usage-switch-target">
              {t("admin.llmUsage.switchModel.target", "Target model")}
            </Label>
            <Select value={selected} onValueChange={setTargetModel}>
              <SelectTrigger id="llm-usage-switch-target" className="h-8 text-xs">
                <SelectValue placeholder={t("admin.llmUsage.switchModel.pick", "Pick a model")} />
              </SelectTrigger>
              <SelectContent>
                {models.map((model) => (
                  <SelectItem key={model.model} value={model.model}>
                    {model.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isImage ? (
            <p className="text-muted-foreground">
              {t("admin.llmUsage.switchModel.perImage", "Cost per image: {{cost}}", {
                cost: projection?.perImageMicros != null
                  ? costDisplay({ costMicros: projection.perImageMicros, unpricedTokens: 0 }, locale).text
                  : "—",
              })}
            </p>
          ) : (
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t("admin.llmUsage.switchModel.thinkingShare", "Thinking share {{percent}}%", {
                percent: Math.round(thinkingShare * 100),
              })}
              <input
                type="range"
                min={0}
                max={THINKING_SHARE_STEPS.length - 1}
                step={1}
                value={THINKING_SHARE_STEPS.indexOf(thinkingShare as (typeof THINKING_SHARE_STEPS)[number])}
                onChange={(event) => {
                  const next = THINKING_SHARE_STEPS[Number(event.target.value)];
                  if (next !== undefined) setThinkingShare(next);
                }}
              />
            </label>
          )}
          {projection && !isImage ? (
            <p className="text-xs text-muted-foreground" data-testid="projected-thinking">
              {t("admin.llmUsage.switchModel.thinkingTokens", "{{tokens}} thinking tokens", {
                tokens: projection.thinkingTokens.toLocaleString(locale),
              })}
            </p>
          ) : null}
          <dl className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <dt className="text-muted-foreground">{t("admin.llmUsage.switchModel.today", "Today")}</dt>
              <dd className="font-medium tabular-nums">{today?.text ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t("admin.llmUsage.switchModel.projected", "Projected")}</dt>
              <dd className="font-medium tabular-nums">{projected?.text ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t("admin.llmUsage.switchModel.delta", "Delta")}</dt>
              <dd className="font-medium tabular-nums">
                {deltaMicros > 0 ? "+" : deltaMicros < 0 ? "−" : ""}
                {delta?.text ?? "—"}
              </dd>
            </div>
          </dl>
          <Button type="button" variant="outline" size="sm" className="self-start" asChild>
            <a href={usedIn ? `/prompts?usedIn=${encodeURIComponent(usedIn)}` : "/prompts"}>
              {t("admin.llmUsage.switchModel.openPrompts", "Open in Prompts")}
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
